import React, { useContext, useState } from 'react';
import { Formik, Field, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from 'react-datepicker';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { Button, CustomDropDown, FileUploadButtonMultiple } from '../../../../../shared/components';
import ActionPlanContext from '../ActionPlanContext';
import BasicRadioButton from '../../../../../shared/components/BasicRadioButton';
import ActionPlanItemContext from './ActionPlanItemContext';
import DropdownChecks from '../../../../../shared/components/DropdownChecks';

export interface NewActionPlanDetailFormData {
  dateActionRecorded: Date | null;
  applicablePolicyOrDirective: string | null;
  topic: string | null;
  action: string | null;
  evidence: string | null;
  evidenceAttachment: string | null;
  evidenceAttachmentUpload?: boolean;
  evidenceAttachmentIds: string[];
  externalDocumentLinkIds: string[];
  externalDocumentLink: string | null;
  milestoneDescription: string | null;
  milestoneDate: Date | null;
  responsible: string | null;
  status: string | null;
  priority: string | null;
  id?: number;
}

interface Props {
  handleSubmit: (
    values: NewActionPlanDetailFormData,
    resetFormCallback: () => void,
    attachedEvidenceNew: FormData | null
  ) => void;
  handleCancel: () => void;
  formState?: NewActionPlanDetailFormData;
  language: string;
  readOnly: boolean;
  policyDirectiveText?: string;
  serverError: string;
}

export enum DocumentTypeOptions {
  NewDocument = 1,
  ExistingDocument = 2,
  ExternalLink = 3,
}

export const buildNewActionPlanDetailSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    dateActionRecorded: Yup.date().required(t('required')).typeError(t('required')),
    applicablePolicyOrDirective: Yup.string().required('required'),
    topic: Yup.string().required(t('required')),
    action: Yup.string().required(t('required')),
    evidence: Yup.string().required(t('required')),
    evidenceAttachment: Yup.string().nullable(),
    milestoneDescription: Yup.string().nullable(),
    milestoneDate: Yup.date()
      .nullable()
      .when('milestoneDescription', {
        is: (d: string | null | undefined) => !d,
        then: (dateSchema) => dateSchema.concat(Yup.date().required(t('required'))),
      }),
    responsible: Yup.string().required(t('required')),
    status: Yup.string().required(t('required')),
    priority: Yup.string().required(t('required')),
  });

const NewActionPlanDetailForm: React.FC<Props> = ({
  formState,
  handleSubmit,
  handleCancel,
  language,
  readOnly,
  policyDirectiveText,
  serverError,
}) => {
  const { t } = useTranslation();

  const schema = buildNewActionPlanDetailSchema(t);

  const documentSelectionTypeInitial = () => {
    if (formState) {
      return formState.evidenceAttachmentIds.length > 0 ||
        formState.externalDocumentLinkIds.length === 0
        ? DocumentTypeOptions.NewDocument
        : DocumentTypeOptions.ExternalLink;
    }
    return 0;
  };

  const fileUpload = new FormData();

  const formInit: NewActionPlanDetailFormData = {
    id: formState ? formState.id : undefined,
    dateActionRecorded: formState ? formState.dateActionRecorded : new Date(),
    applicablePolicyOrDirective: formState ? formState.applicablePolicyOrDirective : '',
    topic: formState ? formState.topic : '',
    action: formState ? formState.action : '',
    evidence: formState ? formState.evidence : '',
    evidenceAttachment: formState ? formState.evidenceAttachment : null,
    evidenceAttachmentUpload: false,
    externalDocumentLink: formState ? formState.externalDocumentLink : '',
    milestoneDescription: formState ? formState.milestoneDescription : '',
    milestoneDate: formState ? formState.milestoneDate : null,
    responsible: formState ? formState.responsible : '',
    status: formState ? formState.status : '',
    priority: formState ? formState.priority : '',
    evidenceAttachmentIds: formState ? formState.evidenceAttachmentIds.concat(['']) : [''],
    externalDocumentLinkIds: formState ? formState.externalDocumentLinkIds.concat(['']) : [],
  };
  const [formData, setFormData] = useState<FormData | null>(null);
  const { aplicablePolicyOptions, indicators } = useContext(ActionPlanContext);
  const [documentSelectionType, setDocumentSelectionType] = useState<DocumentTypeOptions | 0>(
    documentSelectionTypeInitial()
  );

  const [fileDocuments] = useState<string[]>([]);
  const [errorUpload, setErrorUpload] = useState<string | null>(null);

  const aplicablePolicyOrDirectives = aplicablePolicyOptions
    .filter((o) => o.language === language || !o.language)
    .sort((a, b) => a.policyOrder - b.policyOrder || a.directiveOrder - b.directiveOrder)
    .map((o) => ({
      key: `${o.policyId}|${o.directiveId || ''}`,
      value: `${o.policyTitle} ${o.directiveTitle || ''}`,
    }));

  const getLocalizedIndicators = (indicatorName: string, lang: string) =>
    indicators
      .filter((o) => (o.language === lang || !o.language) && o.name === indicatorName)
      .sort((a, b) => {
        if (a.value > b.value) {
          return 1;
        }
        if (a.value < b.value) {
          return -1;
        }
        return 0;
      });

  const setDocumentsSelected = (
    id: string,
    checked: boolean,
    formik: FormikProps<NewActionPlanDetailFormData>
  ) => {
    let newValues;

    if (!checked) {
      newValues = [...formik.values.evidenceAttachmentIds]
        .filter((x) => x !== id)
        .filter((x) => x !== 'none');
    } else {
      newValues = [...formik.values.evidenceAttachmentIds, id].filter((x) => x !== 'none');
    }
    if (newValues.length === 0) {
      newValues = ['none'];
    }

    formik.setFieldValue('evidenceAttachmentIds', newValues);
  };

  const setLinksSelected = (
    id: string,
    checked: boolean,
    formik: FormikProps<NewActionPlanDetailFormData>
  ) => {
    let newValues;

    if (!checked) {
      newValues = [...formik.values.externalDocumentLinkIds].filter((x) => x !== id);
    } else {
      newValues = [...formik.values.externalDocumentLinkIds, id];
    }

    formik.setFieldValue('externalDocumentLinkIds', newValues);
  };

  const { operationActionPlanDocuments, operationActionPlanExternalLinks } =
    useContext(ActionPlanItemContext);
  const clearFormUpload = () => {
    setFormData(null);
  };

  const uploadError = (textError: string) => {
    clearFormUpload();
    let error = textError;
    if (error.toLocaleLowerCase() === 'oversize') {
      error = t('uploadMaxSizeError30');

      const opLen = operationActionPlanDocuments.length;
      for (let i = 0; i < opLen; i += 1) {
        operationActionPlanDocuments.pop();
      }
    }

    setErrorUpload(error);
  };

  const setDocumentsSelectedNewFiles = (
    fData: FormData,
    formik: FormikProps<NewActionPlanDetailFormData>
  ) => {
    const attachments = operationActionPlanDocuments.filter((d) => d.id === 0);
    const opLen = attachments.length;
    for (let i = 0; i < opLen; i += 1) {
      operationActionPlanDocuments.pop();
    }

    const files = fData.getAll('files') as File[];
    files.forEach((f) => {
      fileDocuments.push((f as File).name);
      const adocument = {
        id: 0,
        documentName: f.name,
        documentLink: '',
      };

      operationActionPlanDocuments.push(adocument);
    });

    let newValues = formik.values.evidenceAttachmentIds.filter((n) => !n.startsWith('_0_'));
    const attachmentIds = operationActionPlanDocuments.filter((d) => d.id === 0);
    attachmentIds.forEach((d) => {
      const id = `_0_${d.documentName}`;
      newValues = [...newValues, id];
    });

    formik.setFieldValue('evidenceAttachmentIds', newValues);
  };

  const preUpload = (fData: FormData) => {
    setErrorUpload(null);
    setFormData(fData);
  };
  return (
    <Formik
      initialValues={formInit}
      validationSchema={schema}
      onSubmit={(values, { resetForm }) => {
        const payload = { ...values } as NewActionPlanDetailFormData;
        if (documentSelectionType === DocumentTypeOptions.NewDocument) {
          payload.evidenceAttachmentIds = payload.evidenceAttachmentIds.filter((a) => a !== '');
          payload.externalDocumentLinkIds = [];
        }
        if (documentSelectionType === DocumentTypeOptions.ExternalLink) {
          payload.externalDocumentLinkIds = payload.externalDocumentLinkIds.filter((a) => a !== '');
          payload.evidenceAttachmentIds = [];
        }
        handleSubmit(
          payload,
          resetForm,
          documentSelectionType === DocumentTypeOptions.NewDocument ? formData : null
        );
        clearFormUpload();
      }}
      onReset={() => {
        // Formik's resetForm() already invokes this onReset handler itself
        // (see formik/dist/formik.cjs.development.js) - calling resetForm()
        // again from in here re-enters it and recurses until the call stack
        // overflows. handleReset (bound to the form's native reset event)
        // already calls resetForm() before this runs, so nothing here needs
        // to reset the form again.
        handleCancel();
      }}
    >
      {(formik) => (
        <form className="form" onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
          <div className="modal__body">
            <div className="spr-modal modal-dialog--custom-filter">
              <div className="form-row">
                <div className="col-sm-6">
                  <label className="col-form-label" htmlFor="dateActionRecorded">
                    {t('dateActionRecorded')}
                    <span className="error"> *</span>
                  </label>
                  <div className="input-group input-group-date-picker-wrapper">
                    <div className="form__form-group-field form__date-picker-wrapper">
                      <DatePicker
                        autoComplete="off"
                        id="dateActionRecorded"
                        name="dateActionRecorded"
                        selected={formik.values.dateActionRecorded}
                        onChange={(date: Date | null) =>
                          formik.setFieldValue('dateActionRecorded', date)
                        }
                        onBlur={() => {
                          formik.setTouched({ ...formik.touched, dateActionRecorded: true });
                        }}
                        dateFormat="dd/MM/yyy"
                        className="form-control-date-picker"
                        placeholderText={t('from')}
                        disabled={readOnly}
                      />
                      <div className="form__form-group-icon">
                        <FontAwesomeIcon icon={faCalendar} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <label className="col-form-label" htmlFor="applicablePolicyOrDirective">
                    {t('applicablePolicyOrDirective')}
                    <span className="error"> *</span>
                  </label>
                  {readOnly && policyDirectiveText ? (
                    <Field
                      id="applicablePolicyOrDirective"
                      name="applicablePolicyOrDirective"
                      placeholder=""
                      disabled={readOnly}
                      value={policyDirectiveText}
                    />
                  ) : (
                    <div className="select-wrapper">
                      <Field
                        as="select"
                        id="applicablePolicyOrDirective"
                        name="applicablePolicyOrDirective"
                        disabled={readOnly}
                      >
                        <option value="">{t('selectOption')}</option>
                        {aplicablePolicyOrDirectives.map((pd) => (
                          <option value={pd.key}>{pd.value}</option>
                        ))}
                      </Field>
                    </div>
                  )}
                </div>

                <div className="col-sm-6">
                  {formik.touched.dateActionRecorded && formik.errors.dateActionRecorded && (
                    <span className="form__form-group-error">
                      {formik.errors.dateActionRecorded}
                    </span>
                  )}
                </div>
                <div className="col-sm-6">
                  {formik.touched.applicablePolicyOrDirective &&
                    formik.errors.applicablePolicyOrDirective && (
                      <span className="form__form-group-error">
                        {formik.errors.applicablePolicyOrDirective}
                      </span>
                    )}
                </div>
              </div>

              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label" htmlFor="topic">
                    {t('topic')}
                    <span className="error"> *</span>
                  </label>
                  <Field
                    id="topic"
                    name="topic"
                    placeholder=""
                    maxLength={500}
                    disabled={readOnly}
                  />
                </div>
                {formik.touched.topic && formik.errors.topic && (
                  <div className="col-sm-12">
                    <span className="form__form-group-error">{formik.errors.topic}</span>
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label" htmlFor="action">
                    {t('action')}
                    <span className="error"> *</span>
                  </label>
                  <Field
                    as="textarea"
                    id="action"
                    name="action"
                    maxLength={2000}
                    disabled={readOnly}
                  />
                </div>
                {formik.touched.action && formik.errors.action && (
                  <div className="col-sm-12">
                    <span className="form__form-group-error">{formik.errors.action}</span>
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label" htmlFor="evidence">
                    {t('evidence')}
                    <span className="error"> *</span>
                  </label>
                  <Field
                    id="evidence"
                    name="evidence"
                    placeholder=""
                    maxLength={500}
                    disabled={readOnly}
                  />
                </div>
                {formik.touched.evidence && formik.errors.evidence && (
                  <div className="col-sm-12">
                    <span className="form__form-group-error">{formik.errors.evidence}</span>
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label" htmlFor="evidence">
                    {t('evidenceDocument')}
                  </label>
                </div>

                <div className="col-sm-1 d-flex align-content-center flex-wrap">
                  <BasicRadioButton
                    id="newEvidenceDoc"
                    name="feedbackType"
                    label=""
                    radioValue={DocumentTypeOptions.NewDocument}
                    active={documentSelectionType === DocumentTypeOptions.NewDocument}
                    onChange={() => {
                      setDocumentSelectionType(DocumentTypeOptions.NewDocument);
                    }}
                  />
                </div>
                <div className="col-sm-11 d-flex mb-2">
                  <input
                    placeholder={
                      formData
                        ? (formData.getAll('files') as File[]).map((a) => a.name).join(', ')
                        : t('attachDocument')
                    }
                    type="text"
                    readOnly
                  />
                  <FileUploadButtonMultiple
                    htmlId="btnUpload"
                    text={t('upload')}
                    onClick={(fData: FormData) => {
                      preUpload(fData);
                      formik.setTouched({
                        ...formik.touched,
                        evidenceAttachmentUpload: true,
                      });
                      setDocumentsSelectedNewFiles(fData, formik);
                    }}
                    onError={uploadError}
                    className="upload-flow-documents"
                    disabled={readOnly || documentSelectionType !== DocumentTypeOptions.NewDocument}
                    style={{ padding: '4px 25px', width: '100%' }}
                    formData={fileUpload}
                  />
                </div>

                <div className="col-sm-1 d-flex align-content-center flex-wrap">
                  <span />
                </div>
                <div className="col-sm-11 select-wrapper mb-2">
                  <CustomDropDown
                    text={
                      formik.values.evidenceAttachmentIds !== undefined &&
                      operationActionPlanDocuments !== undefined &&
                      formik.values.evidenceAttachmentIds.length > 1 &&
                      operationActionPlanDocuments.length > 0
                        ? `${formik.values.evidenceAttachmentIds.length - 1} ${t('selected')}`
                        : t('selectTheDocuments')
                    }
                    className="custom-filters-dropdown mt-1"
                    disabled={readOnly || documentSelectionType !== DocumentTypeOptions.NewDocument}
                  >
                    <DropdownChecks
                      elements={operationActionPlanDocuments.map((x) => ({
                        id: x.id === 0 ? `_0_${x.documentName}` : x.id.toString(),
                        name: x.documentName,
                      }))}
                      selected={formik.values.evidenceAttachmentIds}
                      setSelected={(id, checked) => {
                        setDocumentsSelected(id, checked, formik);
                      }}
                      translate={false}
                    />
                  </CustomDropDown>
                  {errorUpload && (
                    <span className="form__form-group-error action-plan-evidence-document-max-size">
                      {errorUpload}
                    </span>
                  )}
                </div>

                <div className="col-sm-1 d-flex align-content-center flex-wrap">
                  <BasicRadioButton
                    id="rb_externalLink"
                    name="feedbackType"
                    label=""
                    radioValue={DocumentTypeOptions.ExternalLink}
                    active={documentSelectionType === DocumentTypeOptions.ExternalLink}
                    onChange={() => {
                      setDocumentSelectionType(DocumentTypeOptions.ExternalLink);
                    }}
                  />
                </div>
                <div className="col-sm-11 select-wrapper">
                  {/* <Field
                        name="externalDocumentLink"
                        placeholder="External Link"
                        disabled={
                          readOnly || documentSelectionType !== DocumentTypeOptions.ExternalLink
                        }
                        style={{ width: '100%' }}
                      />  */}
                  <CustomDropDown
                    text={
                      formik.values.externalDocumentLinkIds !== undefined &&
                      formik.values.externalDocumentLinkIds.length > 1
                        ? `${formik.values.externalDocumentLinkIds.length - 1} ${t('selected')}`
                        : t('selectTheLinks')
                    }
                    className="custom-filters-dropdown mt-1"
                    disabled={
                      readOnly || documentSelectionType !== DocumentTypeOptions.ExternalLink
                    }
                  >
                    <DropdownChecks
                      elements={operationActionPlanExternalLinks.map((x) => ({
                        id: x.id ? x.id.toString() : '',
                        name: x.documentName,
                      }))}
                      selected={formik.values.externalDocumentLinkIds}
                      setSelected={(id, checked) => {
                        setLinksSelected(id, checked, formik);
                      }}
                      translate={false}
                    />
                  </CustomDropDown>
                </div>
              </div>

              <div className="form-row">
                <div className="col-sm-7">
                  <label className="col-form-label" htmlFor="milestoneDescription">
                    {t('milestoneDescription')}
                    <span className="error"> *</span>
                  </label>
                  <div className="row">
                    <div className="col-9">
                      <Field
                        id="milestoneDescription"
                        name="milestoneDescription"
                        placeholder=""
                        maxLength={200}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="col-3 d-flex align-content-center flex-wrap">
                      <span>and/or</span>
                    </div>
                  </div>
                </div>
                <div className="col-sm-5">
                  <label className="col-form-label" htmlFor="milestoneDate">
                    {t('milestoneDate')}
                    <span className="error"> *</span>
                  </label>
                  <div className="input-group input-group-date-picker-wrapper">
                    <div className="form__form-group-field form__date-picker-wrapper">
                      <DatePicker
                        autoComplete="off"
                        id="milestoneDate"
                        name="milestoneDate"
                        selected={formik.values.milestoneDate}
                        onChange={(date: Date | null) =>
                          formik.setFieldValue('milestoneDate', date)
                        }
                        onBlur={() => {
                          formik.setTouched({ ...formik.touched, milestoneDate: true });
                        }}
                        dateFormat="dd/MM/yyy"
                        className="form-control-date-picker"
                        placeholderText={t('from')}
                        disabled={readOnly}
                      />
                      <div className="form__form-group-icon">
                        <FontAwesomeIcon icon={faCalendar} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-7">
                  {formik.touched.milestoneDescription && formik.errors.milestoneDescription && (
                    <span className="form__form-group-error">
                      {formik.errors.milestoneDescription}
                    </span>
                  )}
                </div>
                <div className="col-sm-5">
                  {formik.touched.milestoneDate && formik.errors.milestoneDate && (
                    <span className="form__form-group-error">{formik.errors.milestoneDate}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="col-sm-12">
                  <label className="col-form-label" htmlFor="responsible">
                    {t('responsible')}
                    <span className="error"> *</span>
                  </label>
                  <Field
                    id="responsible"
                    name="responsible"
                    placeholdedebr=""
                    maxLength={200}
                    disabled={readOnly}
                  />
                </div>
                {formik.touched.responsible && formik.errors.responsible && (
                  <div className="col-sm-12">
                    <span className="form__form-group-error">{formik.errors.responsible}</span>
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="col-sm-6">
                  <label className="col-form-label" htmlFor="status">
                    {t('status')}
                    <span className="error"> *</span>
                  </label>
                  <div className="select-wrapper">
                    <Field as="select" id="status" name="status" disabled={readOnly}>
                      <option value="">{t('selectOption')}</option>
                      {getLocalizedIndicators('actionPlanStatus', language).map((i) => (
                        <option value={i.value} key={`status_opt_${i.value}`}>
                          {i.description}
                        </option>
                      ))}
                    </Field>
                  </div>
                </div>
                <div className="col-sm-6">
                  <label className="col-form-label" htmlFor="priority">
                    {t('priority')}
                    <span className="error"> *</span>
                  </label>
                  <div className="select-wrapper">
                    <Field as="select" id="priority" name="priority" disabled={readOnly}>
                      <option value="">{t('selectOption')}</option>
                      {getLocalizedIndicators('priority', language).map((i) => (
                        <option value={i.value} key={`priority_opt_${i.value}`}>
                          {i.description}
                        </option>
                      ))}
                    </Field>
                  </div>
                </div>
                <div className="col-sm-6">
                  {formik.touched.status && formik.errors.status && (
                    <span className="form__form-group-error">{formik.errors.status}</span>
                  )}
                </div>
                <div className="col-sm-6">
                  {formik.touched.priority && formik.errors.priority && (
                    <span className="form__form-group-error">{formik.errors.priority}</span>
                  )}
                </div>
              </div>
              {serverError && (
                <div className="form-row">
                  <div className="col-sm-12">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="col-form-label">
                      <span className="error">{serverError}</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="modal__footer modal__foote--custom-filter">
            <Button
              key="btnCancel"
              className={!readOnly ? 'btn-tertiary' : 'btn-primary'}
              type="reset"
              text={!readOnly ? t('cancel') : t('close')}
            />
            {!readOnly && (
              <Button
                key="btnSave"
                className="btn-primary"
                type="submit"
                disabled={!(formik.isValid && (formik.dirty || formData))}
                text={t('save')}
              />
            )}
          </div>
        </form>
      )}
    </Formik>
  );
};

export default NewActionPlanDetailForm;
